/**
 * Dictionary-based chord detection from MIDI notes.
 *
 * Detection cascade:
 *   1. Full chords (triads, 7ths), exact pattern match
 *   2. Partial chords (power chords), common incomplete structures
 *   3. Interval structures (octave, fifth, fourth), 2-note pairs
 *
 * Supports inversions, octave doublings, confidence scoring,
 * and optional key-aware contextual labels.
 */

import { DetectedKey, diatonicPitchClasses } from './key';
import { Note } from './note';

const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

// ── Categories ─────────────────────────────────────────────────

export type HarmonyCategory =
  | 'full-chord'
  | 'partial-chord'
  | 'interval-structure';

// ── Chord quality types ────────────────────────────────────────

export type ChordQuality =
  | 'major'
  | 'minor'
  | 'diminished'
  | 'augmented'
  | 'sus2'
  | 'sus4'
  | '7'
  | 'maj7'
  | 'm7'
  | 'm7b5'
  | 'dim7';

const QUALITY_SUFFIX: Record<ChordQuality, string> = {
  major: 'maj',
  minor: 'm',
  diminished: 'dim',
  augmented: 'aug',
  sus2: 'sus2',
  sus4: 'sus4',
  '7': '7',
  maj7: 'maj7',
  m7: 'm7',
  m7b5: 'm7b5',
  dim7: 'dim7',
};

// ── Chord pattern dictionary ───────────────────────────────────

type ChordPattern = {
  readonly quality: ChordQuality;
  readonly intervals: readonly number[];
};

const TRIAD_PATTERNS: ChordPattern[] = [
  { quality: 'major', intervals: [0, 4, 7] },
  { quality: 'minor', intervals: [0, 3, 7] },
  { quality: 'diminished', intervals: [0, 3, 6] },
  { quality: 'augmented', intervals: [0, 4, 8] },
  { quality: 'sus2', intervals: [0, 2, 7] },
  { quality: 'sus4', intervals: [0, 5, 7] },
];

const SEVENTH_PATTERNS: ChordPattern[] = [
  { quality: 'maj7', intervals: [0, 4, 7, 11] },
  { quality: '7', intervals: [0, 4, 7, 10] },
  { quality: 'm7', intervals: [0, 3, 7, 10] },
  { quality: 'm7b5', intervals: [0, 3, 6, 10] },
  { quality: 'dim7', intervals: [0, 3, 6, 9] },
];

// Sevenths first, prefer more specific matches
const CHORD_PATTERNS = [...SEVENTH_PATTERNS, ...TRIAD_PATTERNS];

// ── Output type ────────────────────────────────────────────────

export type DetectedChord = {
  /** Display label, e.g. "Dm", "G7", "D/F#", "D5", "oct A" */
  name: string;
  /** Pitch class of the root (0–11) */
  root: number;
  /** Pitch class of the bass note (0–11) */
  bass: number;
  /** Detection category */
  category: HarmonyCategory;
  /** Chord quality (for full/partial chords) or interval type */
  subtype: string;
  /** Pattern intervals from root, e.g. [0, 4, 7] */
  intervals: readonly number[];
  /** Unique pitch classes present in the input */
  pitchClasses: number[];
  /** 0 = root position, 1 = first inversion, etc. */
  inversion: number;
  /** Confidence score 0–1 */
  confidence: number;
  /** Key-aware label (only set when key context provided) */
  contextualLabel?: string;
};

// ── Chord value object ─────────────────────────────────────────

export class Chord {
  private constructor(private readonly _notes: readonly Note[]) {}

  static fromMidi(...midi: number[]): Chord {
    if (midi.length === 0) throw new Error('Chord requires at least one note');
    return new Chord(midi.map(Note.fromMidi));
  }

  static fromNotes(notes: readonly Note[]): Chord {
    if (notes.length === 0) throw new Error('Chord requires at least one note');
    return new Chord([...notes]);
  }

  notes(): readonly Note[] {
    return this._notes;
  }

  detect(key?: DetectedKey | null): DetectedChord | null {
    return detectChord(this._notes.map(n => n.toMidi()), key);
  }

  toName(key?: DetectedKey | null): string {
    return this.detect(key)?.name ?? '?';
  }
}

// ── Main API ───────────────────────────────────────────────────

/**
 * Detect harmony from an array of MIDI note numbers.
 * Cascade: full chord → partial chord → interval structure.
 * Returns null if nothing is recognized.
 */
export function detectChord(
  midiNotes: number[],
  key?: DetectedKey | null,
): DetectedChord | null {
  if (midiNotes.length < 2) return null;

  const pcSet = new Set(midiNotes.map(m => m % 12));
  const pitchClasses = [...pcSet].sort((a, b) => a - b);
  if (pitchClasses.length < 1) return null;

  // Bass = lowest sounding note
  const bassPc = midiNotes.reduce((lo, n) => Math.min(lo, n), midiNotes[0]) % 12;

  // 1. Full chord (3+ pitch classes)
  if (pitchClasses.length >= 3) {
    const full = matchFullChord(pitchClasses, bassPc, key);
    if (full) return full;
  }

  // 2. Partial chord (power chord: root + fifth, possibly with octave doublings)
  const partial = matchPartialChord(pitchClasses, bassPc, key);
  if (partial) return partial;

  // 3. Interval structure (2 notes: octave, fifth, fourth)
  if (pitchClasses.length <= 2) {
    const interval = matchInterval(midiNotes, pitchClasses, bassPc);
    if (interval) return interval;
  }

  return null;
}

// ── Full chord matching ────────────────────────────────────────

function matchFullChord(
  pitchClasses: number[],
  bassPc: number,
  key?: DetectedKey | null,
): DetectedChord | null {
  let best: DetectedChord | null = null;

  for (const rootPc of pitchClasses) {
    const observed = pitchClasses
      .map(pc => (pc - rootPc + 12) % 12)
      .sort((a, b) => a - b);

    for (const pattern of CHORD_PATTERNS) {
      const score = scoreFullMatch(observed, pattern.intervals, rootPc, bassPc);
      if (score <= 0) continue;

      if (!best || score > best.confidence) {
        const inversion = detectInversion(rootPc, bassPc, pattern.intervals);
        const label = NOTE_NAMES[rootPc] + QUALITY_SUFFIX[pattern.quality];
        const name = inversion > 0
          ? label + '/' + NOTE_NAMES[bassPc]
          : label;

        best = {
          name,
          root: rootPc,
          bass: bassPc,
          category: 'full-chord',
          subtype: pattern.quality,
          intervals: pattern.intervals,
          pitchClasses,
          inversion,
          confidence: score,
        };
      }
    }
  }

  if (best && key) {
    best.contextualLabel = buildContextualLabel(best, key);
  }

  return best;
}

// ── Partial chord matching ─────────────────────────────────────

function matchPartialChord(
  pitchClasses: number[],
  bassPc: number,
  key?: DetectedKey | null,
): DetectedChord | null {
  // Power chord: exactly 2 pitch classes a fifth apart [0, 7]
  if (pitchClasses.length === 2) {
    for (const rootPc of pitchClasses) {
      const other = pitchClasses.find(pc => pc !== rootPc)!;
      const interval = (other - rootPc + 12) % 12;
      if (interval === 7) {
        const result: DetectedChord = {
          name: NOTE_NAMES[rootPc] + '5',
          root: rootPc,
          bass: bassPc,
          category: 'partial-chord',
          subtype: 'power',
          intervals: [0, 7],
          pitchClasses,
          inversion: rootPc === bassPc ? 0 : 1,
          confidence: 0.7,
        };
        if (key) {
          result.contextualLabel = buildPowerChordContextualLabel(rootPc, key);
        }
        return result;
      }
    }
  }

  return null;
}

// ── Interval structure matching ────────────────────────────────

function matchInterval(
  midiNotes: number[],
  pitchClasses: number[],
  bassPc: number,
): DetectedChord | null {
  // Octave: all notes are the same pitch class
  if (pitchClasses.length === 1) {
    const pc = pitchClasses[0];
    return {
      name: NOTE_NAMES[pc] + ' oct',
      root: pc,
      bass: pc,
      category: 'interval-structure',
      subtype: 'octave',
      intervals: [0],
      pitchClasses,
      inversion: 0,
      confidence: 0.9,
    };
  }

  // Two distinct pitch classes
  if (pitchClasses.length === 2) {
    const interval = (pitchClasses[1] - pitchClasses[0] + 12) % 12;

    // Fifth: interval 7
    if (interval === 7) {
      return {
        name: NOTE_NAMES[pitchClasses[0]] + '5',
        root: pitchClasses[0],
        bass: bassPc,
        category: 'interval-structure',
        subtype: 'fifth',
        intervals: [0, 7],
        pitchClasses,
        inversion: 0,
        confidence: 0.5,
      };
    }

    // Inverted fifth (fourth): interval 5
    if (interval === 5) {
      // The root is the upper note (the fifth is inverted)
      return {
        name: NOTE_NAMES[pitchClasses[1]] + '5',
        root: pitchClasses[1],
        bass: bassPc,
        category: 'interval-structure',
        subtype: 'fifth',
        intervals: [0, 7],
        pitchClasses,
        inversion: 1,
        confidence: 0.5,
      };
    }

    // Fourth: interval 5 already covered as inverted fifth above
    // Major third
    if (interval === 4 || interval === 8) {
      const rootPc = interval === 4 ? pitchClasses[0] : pitchClasses[1];
      return {
        name: NOTE_NAMES[rootPc] + '?',
        root: rootPc,
        bass: bassPc,
        category: 'interval-structure',
        subtype: 'third',
        intervals: [0, 4],
        pitchClasses,
        inversion: 0,
        confidence: 0.4,
      };
    }

    // Minor third
    if (interval === 3 || interval === 9) {
      const rootPc = interval === 3 ? pitchClasses[0] : pitchClasses[1];
      return {
        name: NOTE_NAMES[rootPc] + 'm?',
        root: rootPc,
        bass: bassPc,
        category: 'interval-structure',
        subtype: 'minor-third',
        intervals: [0, 3],
        pitchClasses,
        inversion: 0,
        confidence: 0.4,
      };
    }
  }

  return null;
}

// ── Scoring ────────────────────────────────────────────────────

function scoreFullMatch(
  observed: number[],
  expected: readonly number[],
  rootPc: number,
  bassPc: number,
): number {
  const observedSet = new Set(observed);
  const expectedSet = new Set(expected);

  // All pattern notes must be present
  for (const e of expected) {
    if (!observedSet.has(e)) return 0;
  }

  // No foreign notes allowed (strict V1)
  for (const o of observed) {
    if (!expectedSet.has(o)) return 0;
  }

  // Base score
  let score = 1.0;

  // Bonus: exact cardinality match
  if (observed.length === expected.length) score += 0.10;

  // Bonus: root in bass
  if (rootPc === bassPc) score += 0.05;

  // Bonus: more intervals = more specific
  score += expected.length * 0.01;

  return Math.min(1.0, Math.max(0, score));
}

// ── Inversion detection ────────────────────────────────────────

function detectInversion(
  rootPc: number,
  bassPc: number,
  intervals: readonly number[],
): number {
  for (let i = 0; i < intervals.length; i++) {
    if ((rootPc + intervals[i]) % 12 === bassPc) return i;
  }
  return 0;
}

// ── Key-aware contextual labels ────────────────────────────────

function buildContextualLabel(chord: DetectedChord, key: DetectedKey): string | undefined {
  const diatonic = diatonicPitchClasses(key.tonic, key.mode);

  // Check if all chord pitch classes are diatonic
  const allDiatonic = chord.pitchClasses.every(pc => diatonic.has(pc));
  if (!allDiatonic) return undefined;

  // Return the chord name with a diatonic indicator
  return chord.name;
}

function buildPowerChordContextualLabel(rootPc: number, key: DetectedKey): string | undefined {
  const diatonic = diatonicPitchClasses(key.tonic, key.mode);
  if (!diatonic.has(rootPc)) return undefined;

  // If root + major third is diatonic → major (incomplete)
  const majorThird = (rootPc + 4) % 12;
  const minorThird = (rootPc + 3) % 12;

  if (diatonic.has(majorThird)) {
    return NOTE_NAMES[rootPc] + 'maj';
  }
  if (diatonic.has(minorThird)) {
    return NOTE_NAMES[rootPc] + 'm';
  }

  return undefined;
}

